/**
 * Help content registry for in-app admin tutorials.
 *
 * Each page exposes a structured object:
 *   - title:      page title (en / ja)
 *   - subtitle:   one-line description
 *   - sections:   array of { heading, body, bullets[], tip, warn }
 *   - costNotes:  cost-control specifics (optional)
 *   - faqs:       array of { q, a }
 *
 * Keep tone calm, technical, and professional. No emojis.
 */

export const HELP_CONTENT = {
  /* ──────────────────────────── MISSION CONTROL ──────────────────────────── */
  'mission-control': {
    title:    { en: 'Mission Control — Operator Guide', ja: 'ミッションコントロール — 運用ガイド' },
    subtitle: {
      en: 'Read this before changing modes, rules, or budgets. Mission Control governs the autonomous AI team.',
      ja: 'モード・ルール・予算を変更する前に必ずお読みください。ミッションコントロールは自律AIチームを制御します。',
    },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Mission Control is the cockpit for the 5-agent autonomous operations team (Sora, Rin, Yuki, Hana, Aoi). From here you observe their decisions, raise/lower the operating mode, change runtime rules, and see incidents, cost, and SLOs.',
          ja: 'ミッションコントロールは5名の自律運用AIチーム（Sora・Rin・Yuki・Hana・Aoi）のコックピットです。エージェントの判断を観察し、運用モードの昇降格やランタイムルールの変更、インシデント・コスト・SLOの確認ができます。',
        },
        bullets: [],
      },
      {
        heading: { en: 'Operation Mode & Readiness Gate', ja: '運用モードと準備ゲート' },
        body: {
          en: 'Mode is a 4-step ladder: monitor_train → advisory → safe_control → full_control. You can ONLY advance when all readiness gates are green. The system always allows immediate downgrade for safety.',
          ja: 'モードは monitor_train → advisory → safe_control → full_control の4段階です。すべての準備ゲートがクリアされた場合のみ昇格できます。安全のため、降格はいつでも可能です。',
        },
        bullets: [
          { en: 'observation_coverage — % of real incidents the team has seen (floor 100).', ja: 'observation_coverage — 実インシデント観察率（下限100）。' },
          { en: 'accuracy_evidence — measured agreement vs. ground truth (floor 0.95).',     ja: 'accuracy_evidence — 真値との一致率（下限0.95）。' },
          { en: 'playbook_confidence — Rin-graded playbook score (floor 0.90).',             ja: 'playbook_confidence — Rin評価のプレイブック信頼度（下限0.90）。' },
        ],
        tip: {
          en: 'If a blocker prevents advancing, work on that signal first. Do not lock a rule to bypass a gate.',
          ja: 'ブロッカーがある場合は、まずその指標を改善してください。ゲート回避のためにルールをロックすることは禁止です。',
        },
      },
      {
        heading: { en: 'AI Team', ja: 'AIチーム' },
        body: {
          en: 'Each agent has a fixed role and a separate LLM model so an outage in one provider does not stop the whole team. Sora is the only agent permitted to make final irreversible calls.',
          ja: '各エージェントには固定の役割と独立したLLMモデルが割り当てられており、特定プロバイダに障害が起きてもチーム全体が停止しない構成です。最終的な不可逆判断はSoraのみが実行できます。',
        },
        bullets: [
          { en: 'Sora — Director (Claude Sonnet 4.5). Final adjudication, model governance.', ja: 'Sora — 監督（Claude Sonnet 4.5）。最終判断とモデル統治。' },
          { en: 'Rin — QA Supervisor (GPT-5.1). Cross-checks Sora and worker output.',         ja: 'Rin — QA監督（GPT-5.1）。Soraとワーカー出力の相互検証。' },
          { en: 'Yuki — Data Extraction (GPT-4o-mini). Scheduling and structured pulls.',     ja: 'Yuki — データ抽出（GPT-4o-mini）。スケジュール実行と構造化データの取得を担当。' },
          { en: 'Hana — Doc Interpretation (Gemini 2.5 Flash). Translation and mapping.',     ja: 'Hana — 文書解釈（Gemini 2.5 Flash）。翻訳と項目のマッピングを担当。' },
          { en: 'Aoi — Accuracy QA (Claude Haiku 4.5). Fast classification at scale.',        ja: 'Aoi — 精度QA（Claude Haiku 4.5）。大量データを高速で分類。' },
        ],
      },
      {
        heading: { en: 'Rules Console', ja: 'ルールコンソール' },
        body: {
          en: 'Runtime knobs that change behaviour without a deploy. Each rule has a current value, an allowed set, and an optional admin lock. Locking is for irreversible safety guarantees only.',
          ja: 'デプロイなしで挙動を変更するランタイムノブ。各ルールには現在値・許容値・任意の管理者ロックがあります。ロックは不可逆の安全保証用途のみ。',
        },
        bullets: [
          { en: 'Toggle changes one rule; an audit row is written to ops_rules_audit.',         ja: '変更は1件ずつ実行され、ops_rules_auditに取扱履歴が記録されます。' },
          { en: 'Locked rules need an admin to unlock before any agent can request a change.', ja: 'ロック済みのルールは、エージェントが変更をリクエストする前に管理者による解除が必要です。' },
        ],
        warn: {
          en: 'Never lock the kill switch in the "on" position without first downgrading to monitor_train.',
          ja: 'monitor_trainにダウングレードする前に、キルスイッチをON状態でロックしないでください。',
        },
      },
      {
        heading: { en: 'Incidents · Cost Budget · SLO', ja: 'インシデント・コスト予算・SLO' },
        body: {
          en: 'These three cards tell you the system is healthy: open incidents trending down, cost under budget, SLOs within burn limits.',
          ja: 'この3枚のカードでシステムの健全性を判断できます。未解決インシデントが減少傾向で、予算内に収まり、SLOのバーン率が許容範囲内であれば正常です。',
        },
        bullets: [
          { en: 'Incidents: any "open" item lives in mc_incidents; expect Rin to triage within 1 cycle.', ja: 'インシデント：openステータスのものはmc_incidentsに保存されます。Rinが1サイクル以内に分類を行います。' },
          { en: 'Cost: tracked per-provider in USD; 80% triggers an amber alert, 100% throttles workers (Sora exempt).', ja: 'コスト：プロバイダ別にUSDで追跡。80%到達で警告を表示、100%到達でワーカーを抑制します（Soraは除く）。' },
          { en: 'SLO: 7-day rolling success rate per operation; red means burn-rate exceeded.', ja: 'SLO：操作別の7日間ローリング成功率。赤色表示はバーン率超過を意味します。' },
        ],
      },
    ],
    costNotes: {
      en: [
        'Daily / monthly budgets are stored in the mc_cost_budget rule. Edit via Rules Console.',
        'When throttled, only Sora is allowed to consume tokens (final-judgment lane).',
        'A failed agent call counts against the budget. Set retries conservatively.',
        'Always prefer model_provider_default rotation over raising the budget when costs spike.',
      ],
      ja: [
        '日次・月次予算は mc_cost_budget ルールに保存。ルールコンソールで編集します。',
        '抑制中はSoraのみがトークンを消費可 (最終判断レーン)。',
        '失敗呼び出しも予算に計上されます。リトライは控えめに。',
        'コスト急増時は予算引き上げよりも model_provider_default のローテーションを優先してください。',
      ],
    },
    faqs: [
      {
        q: { en: 'Can I skip a readiness gate?', ja: '準備ゲートをスキップできますか？' },
        a: {
          en: 'No. Gates are AND-combined and enforced server-side in services/ops_mode.py.',
          ja: 'できません。ゲートはAND条件で結合され、サーバ側（services/ops_mode.py）で強制的にチェックされます。',
        },
      },
      {
        q: { en: 'What happens at full_control?', ja: 'full_controlでは何が起きますか？' },
        a: {
          en: 'Tier-2 autonomous actions execute without human confirmation. Only enable after a full safe_control soak.',
          ja: 'Tier-2の自律行動が人手確認なしで実行されます。safe_controlの十分な実績後にのみ有効化してください。',
        },
      },
    ],
  },

  /* ──────────────────────────── AUTOMATION CENTER ──────────────────────────── */
  'automation-center': {
    title:    { en: 'ETC利用照会サービス（MEISAI） Automation Center — Operator Guide', ja: 'MEISAI自動化センター　—　運用ガイド' },
    subtitle: {
      en: 'Daily pulls, queue pauses, pause-skip logs, and the C8/MEISAI observer enforcement status.',
      ja: '日次取得、キュー一時停止、pause_skipログ、C8/MEISAIオブザーバの状態を管理します。',
    },
    sections: [
      {
        heading: { en: 'Queue pauses', ja: 'キュー一時停止' },
        body: {
          en: 'A queue pause stops one specific runner (e.g., daily_meisai_pull) without taking the whole system offline. Always provide a reason — it is written to the audit log.',
          ja: '特定ランナー（例：daily_meisai_pull）のみを停止し、システム全体を停止せずに保護します。理由は必須で、取扱履歴に記録されます。',
        },
        bullets: [
          { en: 'Pauses are AND-combined with C8 gate and observer logs.',  ja: '一時停止はC8ゲート、オブザーバログとAND結合されます。' },
          { en: 'TTL defaults to 24h; renew explicitly if longer is needed.', ja: 'TTLは初期値24時間。延長時は明示的に再設定してください。' },
        ],
        warn: {
          en: 'Pauses are advisory only when AUTOMATION_PAUSE_OBSERVE_* flags are false. Confirm enforcement state on the page.',
          ja: 'AUTOMATION_PAUSE_OBSERVE_*がfalseの場合は助言のみです。ページで強制状態を確認してください。',
        },
      },
      {
        heading: { en: 'Pause-skip logs', ja: '一時停止スキップログ' },
        body: {
          en: 'Every time a runner asks the central helper "should I pause?", the answer is appended to pause_skip_logs with a 365-day TTL. Use this stream to verify observers are firing before promoting to enforcement.',
          ja: 'ランナーが中央ヘルパーに「停止すべきか」を問い合わせるたびに、その結果がpause_skip_logs（保存期間365日）へ記録されます。強制適用前にオブザーバーの動作を確認する際に利用してください。',
        },
      },
    ],
    costNotes: {
      en: ['Pauses do not directly cost money but cancelled pulls may incur partial Stripe usage refunds — check the Billing Readiness page.'],
      ja: ['一時停止自体に直接費用は発生しませんが、取消されたPullがStripeの一部返金を発生させる場合があります。Billing Readinessページで確認してください。'],
    },
    faqs: [
      {
        q: { en: 'How do I promote an observer to enforcement?', ja: 'オブザーバーを強制適用に昇格させるには？' },
        a: {
          en: 'Flip the corresponding AUTOMATION_PAUSE_OBSERVE_* env var to true after a 14-day clean soak, then redeploy. The static import allowlist enforces this.',
          ja: '14日間のクリーンソーク後にAUTOMATION_PAUSE_OBSERVE_*をtrueへ変更し再デプロイ。静的インポート許可リストが強制します。',
        },
      },
    ],
  },

  /* ──────────────────────────── CUSTOMERS ──────────────────────────── */
  'customers': {
    title:    { en: 'Customers — Operator Guide', ja: '顧客管理 — 運用ガイド' },
    subtitle: { en: 'Search, inspect, and impersonate customer accounts for support.', ja: '顧客アカウントの検索、閲覧、サポート用のなりすまし操作を行えます。' },
    sections: [
      {
        heading: { en: 'Search and filters', ja: '検索とフィルタ' },
        body: {
          en: 'Filter by track (Personal / Business / Govt), plan, ETC Usage Inquiry Service (MEISAI) status, or trial state. Pagination is server-side; do not export full table without using the Exports page.',
          ja: 'トラック（Personal / Business / Govt）、プラン、ETC利用照会サービス（MEISAI）状態、トライアル状態でフィルタできます。ページングはサーバー側で処理されます。全件データの出力にはExportsページをご利用ください。',
        },
      },
      {
        heading: { en: 'Customer 360', ja: 'Customer 360' },
        body: {
          en: 'Click any row to open Customer 360 — combined ETC Usage Inquiry Service (MEISAI) activity, billing history, support tickets, fraud scores, and federation status.',
          ja: '行をクリックするとCustomer 360が開きます。ETC利用照会サービス（MEISAI）履歴、課金、サポート、不正スコア、連携状況を一画面に集約。',
        },
      },
    ],
    costNotes: {
      en: ['Impersonation calls are free but logged. Bulk exports cost CPU; throttle if >10k rows.'],
      ja: ['なりすまし操作は無料ですが必ずログ記録されます。大量出力はCPUコスト大、1万件超は分割してください。'],
    },
    faqs: [],
  },

  /* ──────────────────────────── DATA HEALTH ──────────────────────────── */
  'data-health': {
    title:    { en: 'Data Health — Operator Guide', ja: 'データ正常性 — 運用ガイド' },
    subtitle: { en: 'Schema drift, orphan records, and TTL coverage for every collection.', ja: 'スキーマドリフト、孤立レコード、TTLカバレッジを各コレクションで確認。' },
    sections: [
      {
        heading: { en: 'What to do on red', ja: '赤表示の対処' },
        body: {
          en: 'Open the offending collection, run the suggested aggregation in Mongo, then either fix in code or run the supplied repair script. Never delete records without a backup snapshot.',
          ja: '該当コレクションを開き、提案された集計クエリをMongoで実行してください。その後、コード修正または同梱の修復スクリプトで対応します。バックアップを取らずにレコードを削除してはいけません。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── ADMIN OVERVIEW ──────────────────────────── */
  'admin-overview': {
    title:    { en: 'Admin Overview — Operator Guide', ja: '管理ダッシュボード — 運用ガイド' },
    subtitle: { en: 'The morning-coffee health check for the whole platform.', ja: 'プラットフォーム全体の健全性を朝一で確認するページです。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'A single screen that combines the most important KPIs across the customer base, ETC Usage Inquiry Service (MEISAI) runners, billing, and security so the host can spot anomalies before they become tickets.',
          ja: '顧客基盤・ETC利用照会サービス（MEISAI）ランナー・課金・セキュリティの主要KPIを1画面で確認でき、サポートチケットになる前に異常を見つけられます。',
        },
      },
      {
        heading: { en: 'How to use it', ja: '使い方' },
        body: {
          en: 'Scan the KPI strip first (signups, active trials, failed pulls). Anything in amber/red is a click-through into the matching detail page.',
          ja: 'まず上段KPI（新規登録・トライアル中・失敗Pull）を確認し、橙・赤の数値があれば対応する詳細ページへ移動してください。',
        },
        tip: {
          en: 'Open this page at the start and end of every shift. Patterns repeat.',
          ja: '朝・夕の業務開始・終了時に必ず開いてください。パターンは反復します。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── VISITORS & SITE LOG ──────────────────────────── */
  'visitors': {
    title:    { en: 'Visitors & Site Log — Operator Guide', ja: '訪問者・サイトログ — 運用ガイド' },
    subtitle: { en: 'Real-time visitor tracking with country filters, VPN detection, and an AI assessment of intent.', ja: 'リアルタイムで訪問を追跡し、国別フィルタ・VPN検出・AIによる意図解析を備えます。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Five tabs (Live / History / Analytics / Security / AI Assessment) showing every visitor session with IP geo, device, current page, and risk signals. Pass-through architecture — no ETC Usage Inquiry Service (MEISAI) data is exposed here.',
          ja: '5タブ構成（Live / History / Analytics / Security / AI Assessment）で、全訪問セッションのIP・端末・閲覧ページ・リスク信号を表示します。パススルー方式のため、ETC利用照会サービス（MEISAI）データは含まれません。',
        },
        bullets: [
          { en: 'Live: who is on the site right now (5-second poll).',                    ja: 'Live: 現在サイト上にいる訪問者(5秒間隔で更新)。' },
          { en: 'History: searchable archive across 1h / 24h / 7d / 30d windows.',         ja: 'History: 1時間/24時間/7日/30日の検索可能なアーカイブ。' },
          { en: 'Analytics: traffic shape — top pages, referrers, countries, hourly chart.', ja: 'Analytics: アクセス傾向(トップページ・参照元・国・時間別グラフ)。' },
          { en: 'Security: IP blacklist and threat events.',                                ja: 'Security: IPブラックリストと脅威イベント。' },
          { en: 'AI Assessment: Claude-graded intent score per high-engagement visitor.',   ja: 'AI Assessment: エンゲージメントの高い訪問者の意図をClaudeがスコア化。' },
        ],
      },
      {
        heading: { en: 'How to use the country chips', ja: '国別チップの使い方' },
        body: {
          en: 'The "🌐 All" chip is the default — every country counts. Click any country chip to filter to that country only. Click additional chips to multi-select (add more countries). Click a selected chip to remove it. The clear (N) link surfaces how many are currently selected.',
          ja: '初期状態では「🌐 All」が選択され、全国が集計対象です。国チップをクリックすると単一国でフィルタ、さらにクリックで複数国を選択できます。選択済みチップを再クリックで解除。clear (N) リンクで現在の選択数を確認できます。',
        },
        tip: {
          en: 'Numbers in the country chips ARE the source of truth for the period. The Overview "Top Countries" card now matches these counts since 2026-05-20.',
          ja: '国チップの数値が期間内の正確な値です。Overviewの「Top Countries」カードも2026-05-20以降、この数値と一致します。',
        },
      },
      {
        heading: { en: 'Drill-down drawer', ja: 'ドリルダウンドロワー' },
        body: {
          en: 'Click any row to open the per-visitor timeline — every page they visited, referrer, time-on-page, device, IP changes, and AI verdict (if any).',
          ja: '行をクリックすると、その訪問者のタイムラインが開きます。閲覧ページ、参照元、滞在時間、端末、IP変化、AI判定などを時系列で確認できます。',
        },
      },
    ],
    costNotes: {
      en: ['Geo lookups are cached in visitor_geo_cache (TTL 30d). AI Assessment uses Claude Haiku 4.5 (~$0.001/call). Bots are filtered out of "Real Visitors".'],
      ja: ['Geo検索は visitor_geo_cache にキャッシュ(TTL30日)。AI評価はClaude Haiku 4.5(約$0.001/件)。「Real Visitors」ではボットが除外されます。'],
    },
    faqs: [
      {
        q: { en: 'Why does the History count not match Analytics?', ja: 'HistoryとAnalyticsの件数が一致しないのは？' },
        a: { en: 'Both now use the same time-bounded query as of 2026-05-20 — if they still disagree, refresh the page (the cached Analytics card may be 60s stale).', ja: '2026-05-20以降、両方とも同じ時間範囲クエリを使用。それでもずれる場合はキャッシュ(最大60秒)のため再読み込みしてください。' },
      },
    ],
  },

  /* ──────────────────────────── BUSINESS APPLICATIONS ──────────────────────────── */
  'business-applications': {
    title:    { en: 'Business Applications — Operator Guide', ja: '法人申請 — 運用ガイド' },
    subtitle: { en: 'Review and approve / reject incoming Business Suite signups.', ja: '法人スイートの新規申請を審査し、承認または却下します。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Every business that hits /register?intent=business creates an application here. Review the company details, requested card count, and use-case, then approve (grants 365-day trial) or reject with a reason.',
          ja: '/register?intent=business で申し込まれた法人がここに表示されます。社名、希望カード枚数、利用目的を確認の上、承認（365日トライアル付与）または理由を付けて却下してください。',
        },
        bullets: [
          { en: 'Auto-link: applications email-match to an existing user when one exists.', ja: '自動紐付け:申請メールが既存ユーザーと一致すれば自動で連携します。' },
          { en: 'Approval sends a welcome email via the BUSINESS_APPLICATION_APPROVED template.', ja: '承認すると BUSINESS_APPLICATION_APPROVED テンプレートでウェルカムメールが送信されます。' },
          { en: 'Rejection requires a reason — visible to the applicant in the rejection email.', ja: '却下には理由が必須です。申請者に却下メールで通知されます。' },
        ],
        warn: {
          en: 'Do not approve duplicate applications from the same company without checking. Use the search bar to dedupe.',
          ja: '同一企業からの重複申請は、検索バーで確認してから承認してください。',
        },
      },
    ],
    faqs: [
      {
        q: { en: 'Can a customer re-apply after rejection?', ja: '却下後の再申請は可能ですか?' },
        a: { en: 'Yes — after the cooldown shown on their account. The Business Suite plan does NOT activate retroactively.', ja: '可能です。アカウントに表示されるクールダウン期間が経過すれば再申請できます。Business Suiteプランは遡及適用されません。' },
      },
    ],
  },

  /* ──────────────────────────── BILLING READINESS ──────────────────────────── */
  'billing-readiness': {
    title:    { en: 'Billing Readiness — Operator Guide', ja: '請求準備状況 — 運用ガイド' },
    subtitle: { en: 'Pre-flight checks before turning on real Stripe charges for any tenant.', ja: 'テナントごとに実際のStripe課金を有効化する前の事前チェック。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Lists every tenant with a snapshot of: trial status, payment method on file, ETC Usage Inquiry Service (MEISAI) connection health, and pricing tier. A tenant is "ready" only when all four checks are green.',
          ja: 'テナントごとに(1)トライアル状態(2)決済手段(3)ETC利用照会サービス（MEISAI）接続(4)料金階級を一覧表示。4項目すべて緑のテナントが「準備完了」です。',
        },
        bullets: [
          { en: 'Yellow tile = payment method missing — chase via Customer 360 → Send "Add payment" email.', ja: '黄=決済未登録。Customer 360から「決済追加メール」を送付。' },
          { en: 'Red tile = ETC利用照会サービス（MEISAI） connection failing for 7+ days. Open the MEISAI Monitor row.',          ja: '赤=MEISAI接続が7日以上失敗。MEISAI Monitorで詳細確認。' },
        ],
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── BETA OPERATIONS ──────────────────────────── */
  'beta-operations': {
    title:    { en: 'Beta Operations — Operator Guide', ja: 'ベータ運用 — 運用ガイド' },
    subtitle: { en: 'Feature-flag console + opt-in cohort manager for in-flight beta features.', ja: '開発中のベータ機能を管理する機能フラグとコホート管理コンソール。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Add/remove tenants to a beta cohort and flip feature flags without a redeploy. Includes a 24h activity table per flag so you can confirm impact.',
          ja: 'ベータコホートへのテナント追加・削除と機能フラグの即時切替が可能。フラグ別24時間アクティビティ表で影響を確認できます。',
        },
        warn: {
          en: 'Never flip a flag without an owner and an exit criterion documented in the description field.',
          ja: 'フラグを切り替える際は、必ず担当者と終了条件をdescriptionに記載してください。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── EMAIL TEMPLATES ──────────────────────────── */
  'email-templates': {
    title:    { en: 'Email Templates — Operator Guide', ja: 'メールテンプレート — 運用ガイド' },
    subtitle: { en: 'Edit every transactional email (subject + body, EN/JA) without a code deploy.', ja: '全トランザクションメール（件名・本文・EN/JA）をコードデプロイなしで編集できます。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Stores 49 templates in MongoDB with full version history. Click any template to live-preview the rendered HTML with substituted variables, edit subject/body in both languages, and send a test to your own inbox.',
          ja: '49種類のテンプレートをMongoDBに保存（バージョン履歴付き）。各テンプレートをクリックすると、変数置換後のHTMLをプレビュー表示し、件名・本文を両言語で編集、テスト送信できます。',
        },
        bullets: [
          { en: 'History tab: every save creates a snapshot. Roll back with one click.', ja: 'Historyタブ：保存ごとにスナップショット作成。1クリックで以前のバージョンへ復元。' },
          { en: 'Settings tab: global header/footer, brand logo URL, default test recipient.', ja: 'Settingsタブ：全体ヘッダ/フッタ、ブランドロゴURL、テスト送信先を設定。' },
          { en: 'Test Send: dry-run with the live header/footer wrapping.',                  ja: 'Test Send：本番のヘッダ/フッタを適用してドライラン送信。' },
        ],
        tip: {
          en: 'Always Test Send to yourself in both EN and JA before saving — it is the only catch for broken Liquid variables.',
          ja: '保存前にEN/JA両方で必ずテスト送信してください。Liquid変数の不具合を見つけるにはこれしか方法がありません。',
        },
      },
      {
        heading: { en: 'Variables you can use', ja: '使える変数' },
        body: {
          en: 'Common: {{user_name}}, {{user_email}}, {{plan}}, {{trial_end}}, {{login_url}}, {{support_email}}. Per-template variables are listed in the right panel — click "Variables" to expand.',
          ja: '共通変数：{{user_name}}・{{user_email}}・{{plan}}・{{trial_end}}・{{login_url}}・{{support_email}}。テンプレート個別の変数は右パネル「Variables」で確認できます。',
        },
      },
    ],
    costNotes: {
      en: ['Mandrill costs ~$0.20 per 1000 sends. Test Sends count toward your monthly quota — keep them targeted at the host inbox.'],
      ja: ['Mandrillは1000通あたり約$0.20。テスト送信も月次クォータに加算されるため、ホスト受信箱のみへ送信してください。'],
    },
    faqs: [
      {
        q: { en: 'What happens to a template I delete?', ja: '削除したテンプレートはどうなりますか？' },
        a: { en: 'Soft-deleted — kept in `email_templates_deleted` for 90 days. Can be undeleted by support.', ja: 'ソフト削除されます。`email_templates_deleted` に90日間保持され、サポートで復元できます。' },
      },
    ],
  },

  /* ──────────────────────────── OG PREVIEW ──────────────────────────── */
  'og-preview': {
    title:    { en: 'OG Preview — Operator Guide', ja: 'OGプレビュー — 運用ガイド' },
    subtitle: { en: 'Audit every social-share link preview card across the marketing site.', ja: 'マーケティングサイトの全ソーシャル共有カードを一覧で監査できます。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Renders the actual /api/og/image PNG for every route in OG_DATA + PREFIX_FALLBACKS in both EN and JA, so the host can visually confirm titles, descriptions, and layout look correct before pushing a change.',
          ja: 'OG_DATA + PREFIX_FALLBACKSの全ルートについて、実際の /api/og/image PNGを英語・日本語両方で表示します。変更を公開する前に、タイトル・説明・レイアウトを目で確認できます。',
        },
      },
      {
        heading: { en: 'Adding a new route', ja: '新規ルート追加' },
        body: {
          en: 'Mirror the new title + description in TWO places: frontend SEO_DATA (humans) AND backend OG_DATA (bots). See /docs/OG_RULES_HANDOFF.md for the full playbook.',
          ja: 'タイトルと説明を必ず2か所に登録してください:フロントエンドのSEO_DATA(人間用)とバックエンドのOG_DATA(ボット用)。詳しくは /docs/OG_RULES_HANDOFF.md を参照してください。',
        },
      },
    ],
    faqs: [
      {
        q: { en: 'Why does Facebook still show the old card?', ja: 'Facebookが古いカードを表示するのは?' },
        a: { en: 'Facebook caches OG data per URL. Force a re-scrape at https://developers.facebook.com/tools/debug/ — the Cloudflare Worker must also be deployed (see OG_RULES_HANDOFF.md).', ja: 'FacebookはURL別にOGデータをキャッシュします。https://developers.facebook.com/tools/debug/ で再取得を強制してください。Cloudflare Workerのデプロイも必要です(OG_RULES_HANDOFF.md参照)。' },
      },
    ],
  },

  /* ──────────────────────────── EXPORTS ──────────────────────────── */
  'exports': {
    title:    { en: 'Exports — Operator Guide', ja: 'エクスポート — 運用ガイド' },
    subtitle: { en: 'Bulk CSV/Excel exports of customers, trips, billing, audit logs.', ja: '顧客・走行・課金・取扱履歴のCSV/Excel一括出力。' },
    sections: [
      {
        heading: { en: 'How to use', ja: '使い方' },
        body: {
          en: 'Pick the dataset, date range, and format. Exports run as background jobs and email you when ready (large jobs may take 5-10 minutes). Direct downloads from the row link expire after 24h.',
          ja: 'データセット・期間・形式を選んでください。出力処理はバックグラウンドで実行され、完了するとメールで通知します（大規模な場合は5～10分程度かかります）。ダウンロードリンクの有効期限は24時間です。',
        },
        warn: { en: 'PII columns are masked by default. Use the "Include PII" toggle only when legally required and document the reason.', ja: '個人情報カラムは初期状態でマスクされています。法的に必要な場合のみ「Include PII」を有効化し、理由を記録してください。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── REPORTS ──────────────────────────── */
  'reports': {
    title:    { en: 'Reports — Operator Guide', ja: 'レポート — 運用ガイド' },
    subtitle: { en: 'Browse and re-deliver ETC利用照会サービス（MEISAI） usage reports already generated for tenants.', ja: 'テナント向けに生成済みのMEISAI利用レポートを閲覧・再配信できます。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Every report sent by the daily/weekly/monthly scheduler appears here with status (delivered / bounced / quarantined), PDF + CSV links, and a re-send button. Pass-through: the underlying trip data is regenerated on-demand, never stored.',
          ja: '日次・週次・月次スケジューラから送信された全レポートが、配信状況（配信済／バウンス／隔離）、PDF・CSVリンク、再送ボタンとともに表示されます。走行データはリクエストごとに生成し、永続保存しません。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── REPORT FAILURES ──────────────────────────── */
  'report-failures': {
    title:    { en: 'Report Failures — Operator Guide', ja: 'レポート失敗 — 運用ガイド' },
    subtitle: { en: 'Anything the scheduler tried and could not deliver.', ja: 'スケジューラが送信を試みて失敗した全件を表示します。' },
    sections: [
      {
        heading: { en: 'How to triage', ja: 'トリアージ方法' },
        body: {
          en: '1. Sort by error_class. 2. Bulk-retry "transient" rows (Mandrill rate limit, ETC Usage Inquiry Service (MEISAI) 5xx). 3. Hand-investigate "permanent" rows — usually a missing receipt e-mail or a closed account.',
          ja: '1. error_classでソート。2. transient行（Mandrillレート制限・ETC利用照会サービス（MEISAI） 5xx）は一括再試行。3. permanent行は個別調査（多くは受信メール未設定/閉鎖アカウント）。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── REPORT SIMULATOR ──────────────────────────── */
  'report-simulator': {
    title:    { en: 'Report Simulator — Operator Guide', ja: 'レポートシミュレータ — 運用ガイド' },
    subtitle: { en: 'Generate a sample report from a synthetic or real customer without sending it.', ja: '合成または実顧客データからサンプルレポートを生成します（送信なし）。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Pick a tenant + date range, choose synthetic ETC Usage Inquiry Service (MEISAI) rows or live ones, then "Render" to view the PDF + CSV the customer would have received. Nothing is emailed and nothing is persisted.',
          ja: 'テナントと期間を指定し、合成ETC利用照会サービス（MEISAI）行または実データを選択して「Render」で受信予定のPDF/CSVを確認できます。メール送信も永続化も行いません。',
        },
        tip: { en: 'Best tool to verify a template change end-to-end before bulk-sending.', ja: 'テンプレート変更をエンドツーエンドで検証できる最適なツールです。一括送信前に必ずご使用ください。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── QUARANTINE ──────────────────────────── */
  'quarantine': {
    title:    { en: 'Quarantined Reports — Operator Guide', ja: '隔離レポート — 運用ガイド' },
    subtitle: { en: 'Reports the safety pipeline held back for human review before delivery.', ja: '配信前に人手レビューが必要と判定されたレポートを確認する画面。' },
    sections: [
      {
        heading: { en: 'Why a report gets quarantined', ja: 'レポートが隔離される理由' },
        body: {
          en: 'Triggers: PII anomaly, attachment over 25 MB, suspicious trip count delta vs. last period, or template render error. Each row shows the trigger reason.',
          ja: 'トリガー：個人情報異常、添付ファイル25MB超過、前期比走行件数の大幅変動、テンプレート描画エラー。各行にトリガー理由が表示されます。',
        },
        bullets: [
          { en: 'Approve → re-queues for delivery.',          ja: '承認：配信キューへ再投入。' },
          { en: 'Reject → archived; never reaches customer.', ja: '却下：アーカイブされ、顧客に届きません。' },
          { en: 'Edit → opens a hand-corrected version for re-render.', ja: '編集：手動修正版を作成し再描画。' },
        ],
        warn: { en: 'Never approve without opening the PDF first. The trigger reason exists for a reason.', ja: '必ずPDFを開いて確認してから承認してください。トリガーが発動するには理由があります。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── SECURITY EVENTS ──────────────────────────── */
  'security-events': {
    title:    { en: 'Security Events — Operator Guide', ja: 'セキュリティイベント — 運用ガイド' },
    subtitle: { en: 'Audit trail of every privileged action, login, IP change, and threat signal.', ja: '特権操作、ログイン、IP変更、脅威シグナルのすべてを記録した取扱履歴。' },
    sections: [
      {
        heading: { en: 'How to use', ja: '使い方' },
        body: {
          en: 'Search by user, event type, or IP. Each row has a severity badge (info / warn / critical). Critical rows page the on-call automatically.',
          ja: 'ユーザー、イベント種別、IPで検索できます。各行に重要度バッジ（info / warn / critical）が表示され、criticalは自動的にオンコール通知されます。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── SUPPORT MESSAGES ──────────────────────────── */
  'support-messages': {
    title:    { en: 'Support Messages — Operator Guide', ja: 'サポートメッセージ — 運用ガイド' },
    subtitle: { en: 'Inbound contact-form, AI-chat handoff, and in-app support threads.', ja: 'お問い合わせフォーム、AIチャット引き継ぎ、アプリ内サポートのすべてのスレッド。' },
    sections: [
      {
        heading: { en: 'Triaging messages', ja: 'メッセージのトリアージ' },
        body: {
          en: 'New messages land in "Open". Use the Assignee field to claim. Reply within the message panel — the customer receives an email + an in-app notification.',
          ja: '新着メッセージは「Open」に表示されます。Assigneeフィールドで担当者を設定してください。メッセージパネルで返信すると、お客様にメールとアプリ内通知が送信されます。',
        },
        tip: { en: 'The "Quick replies" dropdown pulls from your library — add new ones via Settings → Quick Replies.', ja: '「Quick replies」プルダウンは定型文ライブラリと連動しています。Settings → Quick Repliesから新規追加できます。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── FEDERATION ──────────────────────────── */
  'federation': {
    title:    { en: 'Federation — Operator Guide', ja: 'フェデレーション — 運用ガイド' },
    subtitle: { en: 'NoJo Enterprise / JapanETCcard partner API health and webhook history.', ja: 'NoJo Enterprise / JapanETCcardパートナーAPIの稼働状況とWebhook履歴。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Live dashboard of every federated partner: API key status, webhook delivery success rate, and per-partner usage. Use it to spot partner-side outages before customers report them.',
          ja: '各パートナーのAPIキー状態、Webhook配信成功率、利用状況をリアルタイム表示。顧客からの報告前にパートナー側の障害を検知できます。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── FRAUD WATCH ──────────────────────────── */
  'fraud-watch': {
    title:    { en: 'Fraud Watch — Operator Guide', ja: '不正監視 — 運用ガイド' },
    subtitle: { en: 'High-signal anomalies in signups, ETC利用照会サービス（MEISAI） pulls, and trip patterns.', ja: '新規登録、MEISAI取得、走行パターンの異常を検知。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Aggregates risk signals: disposable email domains, impossible-travel logins, VPN signups during business apply, sudden trip-count spikes. Each row has an investigate / dismiss action.',
          ja: '使い捨てメール、物理的に移動不可能なログイン、法人申請時のVPN使用、走行件数の急増などのリスクシグナルを集約。各行に調査・却下ボタンを表示。',
        },
        warn: { en: 'Dismissing a row teaches the model — be conservative.', ja: '却下するとモデルが学習します。慎重に判断してください。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── ETC Usage Inquiry Service (MEISAI) MONITOR ──────────────────────────── */
  'meisai-monitor': {
    title:    { en: 'ETC利用照会サービス（MEISAI） Monitor — Operator Guide', ja: 'MEISAIモニター — 運用ガイド' },
    subtitle: { en: 'Per-tenant ETC利用照会サービス（MEISAI） session health, last-pull status, and failure reasons.', ja: 'テナント別MEISAIセッション正常性・直近取得状況・失敗原因。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'A row per active ETC Usage Inquiry Service (MEISAI) connection. Color-coded by health: green (last pull <24h success), yellow (1-3 failures), red (4+ failures or expired session). Click a row for the failure detail and a "Re-auth" button.',
          ja: '稼働中の各ETC利用照会サービス（MEISAI）接続を1行で表示。正常性を色分け：緑（24時間以内に成功）、黄（1～3回失敗）、赤（4回以上失敗またはセッション失効）。行をクリックすると失敗詳細と「再認証」ボタンが表示されます。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── JOB MONITOR ──────────────────────────── */
  'job-monitor': {
    title:    { en: 'Job Monitor — Operator Guide', ja: 'ジョブモニター — 運用ガイド' },
    subtitle: { en: 'Background scheduler health: last run, next run, success/failure per job.', ja: 'バックグラウンドスケジューラの正常性。各ジョブの最終実行・次回実行・成功率を表示。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Every APScheduler job (heartbeat, daily_report, weekly_report, federation_webhook_dispatcher, ic_learning_batch, …) listed with timestamps and last-error if any. Hit "Run now" to trigger an ad-hoc execution.',
          ja: '全APSchedulerジョブ（heartbeat・daily_report・weekly_report・federation_webhook_dispatcher・ic_learning_batch等）をタイムスタンプと最終エラー付きで表示。「Run now」ボタンで即座に実行できます。',
        },
        warn: { en: 'Run-now triggers a real job — for daily_report that means real emails will go out. Use sparingly.', ja: 'Run-nowは実ジョブを起動します（daily_reportなら実際にメールが送信されます）。慎重に使用してください。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── HOST TOOLS ──────────────────────────── */
  'host-tools': {
    title:    { en: 'Host Tools — Operator Guide', ja: 'ホストツール — 運用ガイド' },
    subtitle: { en: 'Host-only one-shot maintenance scripts and dangerous knobs.', ja: 'ホスト専用の単発メンテナンススクリプトと危険操作用ノブ。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Convenience UI for scripts under backend/scripts/ that the host occasionally runs — reseed templates, force a federation sync, rebuild visitor stats, etc. Every action is logged to security_events.',
          ja: 'backend/scripts/配下のホスト用スクリプトをUI化。テンプレート再投入、フェデレーション同期、訪問統計再構築など。全操作はsecurity_eventsに記録されます。',
        },
        warn: { en: 'These are LIVE actions on production data. Read the inline confirmation copy before clicking.', ja: '本番データへの実操作です。実行前に確認ダイアログを必ず読んでください。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── HOMEPAGE MEDIA ──────────────────────────── */
  'homepage-media': {
    title:    { en: 'Homepage Media — Operator Guide', ja: 'トップページ素材 — 運用ガイド' },
    subtitle: { en: 'Swap hero images, logo carousels, and testimonial photos without a deploy.', ja: 'ヒーロー画像、ロゴカルーセル、お客様の声の写真など、デプロイ不要で差し替えできます。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Lists every editable image slot on the marketing site with a preview, the current asset URL, and an upload button. New assets are pushed to object storage and the homepage reflects the change within 60 seconds (CDN cache).',
          ja: 'マーケティングサイトの編集可能な画像スロットを一覧表示します。プレビュー、現在のアセットURL、アップロードボタン付き。新しいアセットはオブジェクトストレージへ送信され、CDNキャッシュ経由で60秒以内にサイトへ反映されます。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── TESTIMONIAL TRANSLATIONS ──────────────────────────── */
  'testimonial-translations': {
    title:    { en: 'Testimonial Translations — Operator Guide', ja: 'お客様の声 翻訳 — 運用ガイド' },
    subtitle: { en: 'EN/JA copy management for every testimonial shown on the marketing site.', ja: 'マーケティングサイトに掲載する全お客様の声を英語・日本語で管理します。' },
    sections: [
      {
        heading: { en: 'How to use', ja: '使い方' },
        body: {
          en: 'Each testimonial has source language + translated language slots, plus visibility flags per locale. Use the "AI translate" button for a Claude-generated first draft then hand-edit.',
          ja: '各お客様の声には原文・訳文スロットとロケール別公開フラグがあります。「AI translate」ボタンでClaudeによる下訳を生成し、その後手動で編集してください。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── CLOSED ACCOUNTS ──────────────────────────── */
  'closed-accounts': {
    title:    { en: 'Closed Accounts — Operator Guide', ja: '閉鎖アカウント — 運用ガイド' },
    subtitle: { en: 'Every account that has been deactivated, deleted, or anonymised.', ja: '無効化・削除・匿名化されたアカウントの一覧。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Searchable archive with the close-reason (user-requested, GDPR erasure, fraud, inactivity). Restore is possible for 30 days after closure (except GDPR erasures which are permanent).',
          ja: '閉鎖理由（本人申請・GDPR削除・不正・非アクティブ）で検索できるアーカイブ。閉鎖から30日以内は復元可能（GDPR削除を除く）。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── DIAGNOSTIC 360 ──────────────────────────── */
  'diagnostic-360': {
    title:    { en: 'Diagnostic 360 — Operator Guide', ja: 'Diagnostic 360 — 運用ガイド' },
    subtitle: { en: 'Single-customer 360° health view: ETC利用照会サービス（MEISAI）, billing, reports, support, security.', ja: '顧客ごとの360°ビュー：MEISAI・課金・レポート・サポート・セキュリティ。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Paste any customer identifier (email, user_id, ETC Usage Inquiry Service (MEISAI) account id, Stripe customer id). The page returns every piece of data we hold and shows green/yellow/red dots on each subsystem. Use this when a customer reports any problem.',
          ja: '顧客識別子（メール・user_id・ETC利用照会サービス（MEISAI）アカウントID・Stripe顧客ID）を入力すると、保持する全データと各サブシステムの状態（緑/黄/赤）が表示されます。問い合わせ対応の第一歩として使用してください。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── LAUNCH CHECKLIST ──────────────────────────── */
  'launch-checklist': {
    title:    { en: 'Launch Checklist — Operator Guide', ja: 'ローンチチェックリスト — 運用ガイド' },
    subtitle: { en: 'The complete go-live readiness checklist with auto-checks for each item.', ja: '本番稼働前チェックリスト（全項目を自動判定）。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Every gate that must be green before flipping a major switch (Stripe live mode, federation enable, full_control mode, etc.). Each row has an auto-check button that re-runs the underlying probe.',
          ja: 'Stripe本番モード・フェデレーション有効化・full_controlモードなど、重要な切替前に確認すべき全項目。各行で自動判定を再実行できます。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── ADMIN SETTINGS ──────────────────────────── */
  'admin-settings': {
    title:    { en: 'Admin Settings — Operator Guide', ja: '管理設定 — 運用ガイド' },
    subtitle: { en: 'Global site settings: branding, contact info, social links, business hours.', ja: 'サイト全体の設定：ブランディング・連絡先・SNSリンク・営業時間。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Edit values that show on the marketing site footer + email templates + invoices. Changes propagate to all surfaces within 60 seconds (no deploy needed).',
          ja: 'マーケティングサイトのフッター・メールテンプレート・請求書に表示される項目を編集できます。変更は60秒以内に全画面へ反映されます（デプロイ不要）。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── PHASE B LIVE ──────────────────────────── */
  'phase-b-live': {
    title:    { en: 'Live Form Readiness — Operator Guide', ja: 'ライブ・フォーム準備 — 運用ガイド' },
    subtitle: { en: 'Iter-81 Phase-B "Add Card" runner — live Chromium automation status.', ja: 'Iter-81 Phase-B「カード追加」ランナー — Chromium自動化のライブ状態' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Real-time view of the live add-card runner. Feature-flagged off by default (LIVE_ADD_CARD_RUNNER_ENABLED). Shows last 50 runs with success/fail and screenshots of the Chromium session.',
          ja: 'カード追加ランナーのリアルタイム表示。デフォルトでは機能フラグOFF（LIVE_ADD_CARD_RUNNER_ENABLED）。直近50件の成功/失敗とChromiumセッションのスクリーンショットを表示します。',
        },
        warn: { en: 'Enabling the flag triggers REAL ETC利用照会サービス（MEISAI） authentications. Confirm with the host before flipping.', ja: 'フラグ有効化は実MEISAI認証を実行します。切替前にホストに確認してください。' },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── CUSTOMER-SIDE PAGES ──────────────────────────── */
  'customer-dashboard': {
    title:    { en: 'Your Dashboard — User Guide', ja: 'マイダッシュボード — 使い方ガイド' },
    subtitle: { en: 'Overview of your trips, latest reports, and account status.', ja: '走行履歴、最新レポート、アカウント状態の概要を表示します。' },
    sections: [
      {
        heading: { en: 'What you can do here', ja: 'できること' },
        body: {
          en: 'View your latest trip totals, download the most recent report (PDF/CSV), and jump into ETC card management, billing, or support.',
          ja: '直近の走行合計の確認、最新レポート（PDF/CSV）のダウンロード、ETCカード管理・課金・サポートページへの移動ができます。',
        },
      },
    ],
    faqs: [],
  },
  'customer-billing': {
    title:    { en: 'Billing — User Guide', ja: '料金・お支払い — 使い方ガイド' },
    subtitle: { en: 'Your plan, payment method, invoices, and upgrades.', ja: 'ご契約プラン、お支払い方法、請求書、プランアップグレードについて。' },
    sections: [
      {
        heading: { en: 'Managing your subscription', ja: 'サブスクリプション管理' },
        body: {
          en: 'See your current plan, change payment method, download past invoices, and upgrade/downgrade. Cancellations take effect at the end of the current billing cycle.',
          ja: '現在のプラン確認、支払い方法の変更、過去の請求書ダウンロード、プランのアップグレード/ダウングレードができます。解約は現在の課金サイクル終了時に反映されます。',
        },
      },
    ],
    faqs: [],
  },
  'customer-cards': {
    title:    { en: 'ETC Cards — User Guide', ja: 'ETCカード — 使い方ガイド' },
    subtitle: { en: 'Add, edit, or remove the ETC cards we track for you.', ja: 'Japan Toll Receiptsで管理するETCカードの追加・編集・削除ができます。' },
    sections: [
      {
        heading: { en: 'Adding a card', ja: 'カードの追加' },
        body: {
          en: 'Personal plans: up to 10 ETC cards. Click "Add card", enter the card number and a label, and we will start pulling trips on the next ETC Usage Inquiry Service (MEISAI) cycle.',
          ja: '個人プラン：最大10枚のETCカードを登録できます。「カード追加」をクリックして、カード番号とラベルを入力すると、次回のETC利用照会サービス（MEISAI）サイクルから走行データの取得を開始します。',
        },
      },
    ],
    faqs: [],
  },
  'customer-reports': {
    title:    { en: 'Reports — User Guide', ja: 'レポート — 使い方ガイド' },
    subtitle: { en: 'All your past usage reports and on-demand re-downloads.', ja: '過去の利用レポートといつでも再ダウンロードできます。' },
    sections: [
      {
        heading: { en: 'Re-downloading a report', ja: 'レポートの再ダウンロード' },
        body: {
          en: 'Every PDF + CSV we have sent you is listed here. Re-download anytime — links never expire.',
          ja: 'お送りしたPDF・CSVはすべてこちらに表示されます。リンクは無期限で、いつでも再ダウンロードいただけます。',
        },
      },
    ],
    faqs: [],
  },
  'customer-support': {
    title:    { en: 'Support — User Guide', ja: 'サポート — 使い方ガイド' },
    subtitle: { en: 'Open a support thread or chat with the AI assistant.', ja: 'サポートスレッドを開くか、AIアシスタントにご相談ください。' },
    sections: [
      {
        heading: { en: 'How to reach us', ja: 'お問い合わせ方法' },
        body: {
          en: 'Open a thread for anything billing, ETC Usage Inquiry Service (MEISAI), or account-related. The AI assistant handles common questions instantly; complex tickets are routed to the human team within 24 hours JST business days.',
          ja: '課金・ETC利用照会サービス（MEISAI）・アカウントについてのお問い合わせはスレッドをご利用ください。AIアシスタントが一般的なご質問に即座にお答えし、複雑な内容は営業日24時間以内(JST)に担当者が対応いたします。',
        },
      },
    ],
    faqs: [],
  },

  /* ──────────────────────────── AUDIT LOGS ──────────────────────────── */
  'audit-logs': {
    title:    { en: 'Audit Logs — Operator Guide', ja: '取扱履歴 — 運用ガイド' },
    subtitle: { en: 'Searchable history of every privileged action across the platform.', ja: 'プラットフォーム内のすべての特権操作を検索できる履歴です。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Filter by actor, target, action type, or time window. Each row links to the contextual evidence so you can reconstruct what happened, when, and by whom. Use this when investigating a customer-facing anomaly or preparing for a compliance review.',
          ja: '実行者・対象・操作種別・期間で絞り込めます。各行は関連証拠にリンクしており、何が・いつ・誰によって起きたかを再構成できます。顧客影響のある事象の調査やコンプライアンス監査の準備に活用してください。',
        },
      },
    ],
    faqs: [],
  },
  /* ──────────────────────────── EMAIL LOGS ──────────────────────────── */
  'email-logs': {
    title:    { en: 'Email Logs — Operator Guide', ja: 'メール送信ログ — 運用ガイド' },
    subtitle: { en: 'Every email the platform has sent — with delivery, bounce, and open status.', ja: 'プラットフォームが送信したすべてのメール（配信・バウンス・開封状況付き）。' },
    sections: [
      {
        heading: { en: 'What this page does', ja: 'このページの役割' },
        body: {
          en: 'Search by recipient, template key, or status. Re-send is one click for transient bounces. Permanent bounces are auto-flagged so you can disable that address in the customer record before sending more.',
          ja: '宛先・テンプレートキー・状態で検索できます。一時的なバウンスはワンクリックで再送信。恒久的バウンスは自動でフラグされるため、追加送信前に顧客レコードで該当アドレスを無効化できます。',
        },
      },
    ],
    faqs: [],
  },
  /* ──────────────────────────── DEFAULT FALLBACK ──────────────────────────── */
  '_default': {
    title:    { en: 'Page Guide', ja: 'ページガイド' },
    subtitle: { en: 'This page does not have a dedicated guide yet.', ja: 'このページのガイドはまだありません。' },
    sections: [
      {
        heading: { en: 'General navigation', ja: '基本操作' },
        body: {
          en: 'Use the left sidebar to navigate between admin areas. Press "?" (top-right) on any page to open this guide.',
          ja: '左サイドバーで管理エリアを切り替えます。各ページ右上の「？」でこのガイドを開けます。',
        },
      },
    ],
    faqs: [],
  },
};

export function getHelp(pageKey) {
  return HELP_CONTENT[pageKey] || HELP_CONTENT._default;
}
